import type {
  CreateWorkItemAction,
  ExtendedThalamosUser,
  FormContextData,
} from "@thalamos/aspire-common";
import {
  FDMCA_TEMPLATE_ID,
  formIsCompleted,
  formIsInProgress,
  getLatestVersion,
  liveMcaForms,
} from "@thalamos/aspire-common";

import { admissionFormContextLogic } from "../helpers/formContextLogic.js";

export const getTemplate = (formContext: Omit<FormContextData, "patient">) => {
  const standaloneForm =
    formContext.type === "standalone" ? formContext.forms[0] : undefined;
  if (standaloneForm !== undefined) {
    return {
      template: standaloneForm.formTemplate,
      part: getLatestVersion(standaloneForm).data.length + 1,
    };
  }

  // An MCA assessment is only ever waiting on one of its forms at a time, so the
  // request is described by whichever form still needs signing.
  if (formContext.type === "mca-assessment") {
    const forms = liveMcaForms(formContext.forms);
    const form = forms.find(formIsInProgress) ?? forms[0];

    // An assessment whose forms have all been deleted has no form to describe,
    // so fall back to the one it would be started with rather than failing.
    if (!form) {
      return { template: { id: FDMCA_TEMPLATE_ID, version: "1.0" }, part: 1 };
    }

    return {
      template: form.formTemplate,
      part: getLatestVersion(form).data.length + 1,
    };
  }

  if (formContext.type === "soad-assessment") {
    return {
      template: { id: "soad-report-form", version: "1.0" },
      part: 1,
    };
  }

  return { template: { id: "mha-a2", version: "1.0" }, part: 1 };
};

export function actions(
  formContext: Omit<FormContextData, "patient">,
  user: ExtendedThalamosUser,
): (CreateWorkItemAction | { type: "share" })[] {
  if (formContext.type === "admission") {
    const {
      admissionHasCompletedApplication,
      admissionHasCompletedRecordOfDetention,
      needsPart2Forms,
    } = admissionFormContextLogic(formContext, user);

    const availableActions: (CreateWorkItemAction | { type: "share" })[] = [];

    if (formContext.forms.some((f) => f.status === "finalised")) {
    } else if (admissionHasCompletedRecordOfDetention) {
      availableActions.push({ type: "finalise" });
    } else if (admissionHasCompletedApplication) {
      availableActions.push({ type: "record-of-detention" });
    } else {
      availableActions.push({ type: "application" });
      availableActions.push({ type: "medical-recommendation" });
    }

    for (const form of needsPart2Forms) {
      availableActions.push({
        type: "complete",
        formId: form.id,
        formPart: 2,
        formVersion: form.latestVersion,
      });
    }

    availableActions.push({ type: "share" });

    return availableActions;
  } else if (formContext.type === "police-incident") {
    const availableActions: (CreateWorkItemAction | { type: "share" })[] = [
      { type: "finalise" },
      { type: "share" },
    ];

    return availableActions;
  } else if (formContext.type === "mca-assessment") {
    const forms = liveMcaForms(formContext.forms);

    const availableActions: (CreateWorkItemAction | { type: "share" })[] = forms
      .filter(formIsInProgress)
      .map((form) => ({
        type: "complete",
        formId: form.id,
        formPart: (getLatestVersion(form).data.length || 0) + 1,
        formVersion: form.latestVersion,
      }));

    // Scrutiny covers the assessment as a whole, so it is only offered once
    // every form in it is complete.
    if (forms.length > 0 && forms.every(formIsCompleted)) {
      availableActions.push({ type: "finalise" });
    }

    availableActions.push({ type: "share" });

    return availableActions;
  } else {
    const availableActions: (CreateWorkItemAction | { type: "share" })[] = [];

    const form = formContext.forms[0];

    if (form?.status === "in-progress") {
      availableActions.push({
        type: "complete",
        formId: form.id,
        formPart: (getLatestVersion(form).data.length || 0) + 1,
        formVersion: form.latestVersion,
      });
    }

    if (form?.status === "complete") {
      availableActions.push({ type: "finalise" });
    }

    availableActions.push({ type: "share" });

    return availableActions;
  }
}
